<script setup lang="ts">
import { inject, onMounted, ref, watch, onUnmounted, computed, type Ref } from 'vue'
import type { AudioClipDto } from '../../dtos'
import { audioEngine } from '../../audio/AudioEngine'
import { pixelsPerSecond, useMixStore, STATIC_BASE_URL } from '../../stores/mixStore'
import { useSongStore } from '../../stores/songStore'
import { usePresenceStore } from '../../stores/presenceStore'
import { useUiStore } from '../../stores/uiStore'

const props = withDefaults(defineProps<{
  clip: AudioClipDto
  horizontal?: boolean
  readonly?: boolean
}>(), {
  horizontal: false,
  readonly: false
})

const mixStore = useMixStore()
const songStore = useSongStore()
const presenceStore = usePresenceStore()
const uiStore = useUiStore()
const razorMode = inject<Ref<boolean>>('razorMode', ref(false))
const snapToBars = inject<Ref<boolean>>('snapToBars', ref(false))
const razorHoverPos = ref<number | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
const showDeleteConfirm = ref(false)
const showRenameDialog = ref(false)
const showBounceDialog = ref(false)
const showDuplicateDialog = ref(false)
const showAiEditDialog = ref(false)
const bouncing = ref(false)
const editName = ref('')
const adjustPrompt = ref('')
const adjusting = ref(false)
const adjustError = ref('')

const isDragging = ref(false)
const touchReady = ref(false)
const longPressTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const dragStartPos = ref(0)
const initialTimelineStart = ref(0)
const lastClientX = ref(0)
const lastClientY = ref(0)

const clipStyle = computed(() => {
  if (props.horizontal) {
    return {
      left: `${props.clip.timelineStart * pixelsPerSecond.value}px`,
      width: `${props.clip.duration * pixelsPerSecond.value}px`,
      top: '0',
      bottom: '0'
    }
  }
  return {
    top: `${props.clip.timelineStart * pixelsPerSecond.value}px`,
    height: `${props.clip.duration * pixelsPerSecond.value}px`
  }
})

// Browser canvas size limit (Chrome/Firefox ~32,767, Safari often lower). Cap below safe.
const MAX_CANVAS_PIXELS = 16000

const renderWaveform = async () => {
  if (!canvas.value) return

  try {
    const buffer = await audioEngine.loadClip(props.clip, STATIC_BASE_URL)

    if (props.horizontal) {
      const desiredWidth = Math.max(props.clip.duration * pixelsPerSecond.value, 1)
      const width = Math.min(desiredWidth, MAX_CANVAS_PIXELS)
      const height = 80
      canvas.value.width = width
      canvas.value.height = height

      const ctx = canvas.value.getContext('2d')
      if (!ctx) return

      const peaks = audioEngine.getWaveformData(buffer, width, props.clip.duration, props.clip.sourceOffset)
      ctx.fillStyle = props.clip.isLocked ? '#94a3b8' : '#0d9488'
      ctx.clearRect(0, 0, width, height)

      const mid = height / 2
      peaks.forEach((peak, i) => {
        const h = Math.max(peak * mid, 1)
        ctx.fillRect(i, mid - h, 1, h * 2)
      })
    } else {
      const desiredHeight = Math.max(props.clip.duration * pixelsPerSecond.value, 1)
      const height = Math.min(desiredHeight, MAX_CANVAS_PIXELS)
      const width = 64
      canvas.value.width = width
      canvas.value.height = height

      const ctx = canvas.value.getContext('2d')
      if (!ctx) return

      const peaks = audioEngine.getWaveformData(buffer, height, props.clip.duration, props.clip.sourceOffset)
      ctx.fillStyle = props.clip.isLocked ? '#94a3b8' : '#0d9488'
      ctx.clearRect(0, 0, width, height)

      const mid = width / 2
      peaks.forEach((peak, i) => {
        const w = Math.max(peak * mid, 1)
        ctx.fillRect(mid - w, i, w * 2, 1)
      })
    }
  } catch (e) {
    console.error("Failed to render waveform", e)
  }
}

onMounted(() => {
  renderWaveform()
  window.addEventListener('mousemove', handleMouseMoveGlobal)
  window.addEventListener('mouseup', handleMouseUp)
  window.addEventListener('touchmove', handleTouchMove, { passive: false })
  window.addEventListener('touchend', handleTouchEnd)
})

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMoveGlobal)
  window.removeEventListener('mouseup', handleMouseUp)
  window.removeEventListener('touchmove', handleTouchMove)
  window.removeEventListener('touchend', handleTouchEnd)
  if (longPressTimer.value) clearTimeout(longPressTimer.value)
})

watch(() => props.clip.duration, renderWaveform)
watch(() => props.clip.filePath, renderWaveform)
watch(() => props.clip.isLocked, renderWaveform)
watch(() => props.horizontal, renderWaveform)
watch(pixelsPerSecond, renderWaveform)

const startDrag = (clientX: number, clientY: number, target: EventTarget | null) => {
  if ((target as HTMLElement).closest('button')) return
  if (props.clip.isLocked) return
  isDragging.value = true
  dragStartPos.value = props.horizontal ? clientX : clientY
  initialTimelineStart.value = props.clip.timelineStart
  lastClientX.value = clientX
  lastClientY.value = clientY
  mixStore.dragState = {
    clipId: props.clip.id,
    sourceTrackId: props.clip.mixTrackId,
    destTrackId: props.clip.mixTrackId,
    timelineStart: props.clip.timelineStart,
    duration: props.clip.duration
  }
}

const hitTestDestTrackId = (clientX: number, clientY: number): number | null => {
  const target = document.elementFromPoint(clientX, clientY) as HTMLElement | null
  const lane = target?.closest('[data-track-id]') as HTMLElement | null
  const attr = lane?.dataset.trackId
  return attr ? parseInt(attr, 10) : null
}

const moveDrag = (clientX: number, clientY: number) => {
  if (!isDragging.value) return
  lastClientX.value = clientX
  lastClientY.value = clientY
  const delta = props.horizontal
    ? clientX - dragStartPos.value
    : clientY - dragStartPos.value
  const deltaTime = delta / pixelsPerSecond.value
  let newStart = Math.max(0, initialTimelineStart.value + deltaTime)
  if (snapToBars.value) {
    const bpm = Math.max(20, mixStore.metronomeBpm || 120)
    const beatsPerBar = Math.max(1, mixStore.metronomeBeatsPerBar || 4)
    const secondsPerBar = (60 / bpm) * beatsPerBar
    newStart = Math.round(newStart / secondsPerBar) * secondsPerBar
  }
  props.clip.timelineStart = newStart

  // Update preview state for cross-track hit
  if (mixStore.dragState) {
    const destTrackId = hitTestDestTrackId(clientX, clientY) ?? props.clip.mixTrackId
    mixStore.dragState.destTrackId = destTrackId
    mixStore.dragState.timelineStart = newStart
  }
}

const endDrag = async () => {
  if (!isDragging.value) return
  isDragging.value = false

  const destTrackId = hitTestDestTrackId(lastClientX.value, lastClientY.value)
  const movedTracks = !!destTrackId && destTrackId !== props.clip.mixTrackId
  const movedTime = props.clip.timelineStart !== initialTimelineStart.value

  mixStore.dragState = null

  if (movedTracks) {
    await mixStore.moveClipToTrack(props.clip.id, destTrackId!, movedTime ? props.clip.timelineStart : undefined)
  } else if (movedTime) {
    await mixStore.updateClip(props.clip.id, { timelineStart: props.clip.timelineStart })
  }
}

const getClipRelativeTime = (e: MouseEvent | TouchEvent, el: HTMLElement): number => {
  const rect = el.getBoundingClientRect()
  const clientPos = 'touches' in e ? e.touches[0]! : e
  const px = props.horizontal
    ? clientPos.clientX - rect.left
    : clientPos.clientY - rect.top
  return Math.max(0, Math.min(props.clip.duration, px / pixelsPerSecond.value))
}

const showSplitBlockedDialog = ref(false)

const handleRazorClick = async (e: MouseEvent) => {
  e.stopPropagation()
  if (props.clip.isAiGenerated) {
    showSplitBlockedDialog.value = true
    return
  }
  const el = (e.currentTarget as HTMLElement)
  const splitAt = getClipRelativeTime(e, el)
  // Need a minimum sliver on each side (0.1s)
  if (splitAt < 0.1 || splitAt > props.clip.duration - 0.1) return
  await mixStore.splitClip(props.clip.id, splitAt)
  razorMode.value = false
}

const handleRazorMouseMove = (e: MouseEvent) => {
  const el = (e.currentTarget as HTMLElement)
  razorHoverPos.value = getClipRelativeTime(e, el)
}

const handleRazorMouseLeave = () => {
  razorHoverPos.value = null
}

const handleMouseDown = (e: MouseEvent) => {
  if (props.readonly) return
  if (razorMode.value) return handleRazorClick(e)
  e.stopPropagation()
  startDrag(e.clientX, e.clientY, e.target)
}
const handleMouseMoveGlobal = (e: MouseEvent) => moveDrag(e.clientX, e.clientY)
const handleMouseMoveLocal = (e: MouseEvent) => {
  if (razorMode.value) handleRazorMouseMove(e)
}
const handleMouseUp = () => endDrag()

const handleTouchStart = (e: TouchEvent) => {
  if (props.readonly) return
  const touch = e.touches[0]
  if (!touch) return
  if ((e.target as HTMLElement).closest('button')) return
  const x = touch.clientX
  const y = touch.clientY
  touchReady.value = false
  longPressTimer.value = setTimeout(() => {
    touchReady.value = true
    startDrag(x, y, e.target)
  }, 400)
}

const handleContextMenu = (e: Event) => {
  e.preventDefault()
}
const handleTouchMove = (e: TouchEvent) => {
  const touch = e.touches[0]
  if (!touch) return
  if (!touchReady.value) {
    // User moved before long-press fired — cancel and let scroll happen
    if (longPressTimer.value) {
      clearTimeout(longPressTimer.value)
      longPressTimer.value = null
    }
    return
  }
  e.preventDefault()
  moveDrag(touch.clientX, touch.clientY)
}
const handleTouchEnd = () => {
  if (longPressTimer.value) {
    clearTimeout(longPressTimer.value)
    longPressTimer.value = null
  }
  touchReady.value = false
  endDrag()
}

const confirmDelete = async () => {
  // Check concurrency for the parent Mix
  const mixId = mixStore.currentMix?.id
  if (mixId) {
    const isLocked = await presenceStore.checkConcurrency('Mix', mixId)
    if (isLocked) {
      uiStore.triggerConcurrencyError('Cannot delete clips while other members are active in this mix.')
      return
    }
  }
  showDeleteConfirm.value = true
}

const handleDelete = async () => {
  try {
    await mixStore.deleteClip(props.clip.id)
    showDeleteConfirm.value = false
  } catch (error) {
    console.error(error)
  }
}

const posMins = ref('00')
const posSecs = ref('00')
const posFrac = ref('00')
const posMs = ref('0')

const maxPosition = computed(() => {
  const mixDuration = mixStore.currentMix?.durationSeconds ?? 300
  return mixDuration - props.clip.duration
})

const positionToFields = (seconds: number) => {
  const totalMs = Math.round(seconds * 1000)
  posMins.value = Math.floor(totalMs / 60000).toString().padStart(2, '0')
  posSecs.value = Math.floor((totalMs % 60000) / 1000).toString().padStart(2, '0')
  posFrac.value = Math.floor((totalMs % 1000) / 10).toString().padStart(2, '0')
  posMs.value = (totalMs % 10).toString()
}

const fieldsToSeconds = (): number => {
  const m = Number(posMins.value) || 0
  const s = Number(posSecs.value) || 0
  const f = Number(posFrac.value.padEnd(2, '0').slice(0, 2)) || 0
  const ms = Number(posMs.value) || 0
  return m * 60 + s + f / 100 + ms / 1000
}

const applyPosition = async (newSeconds: number) => {
  const clamped = Math.max(0, Math.min(maxPosition.value, Math.round(newSeconds * 1000) / 1000))
  positionToFields(clamped)
  await mixStore.updateClip(props.clip.id, { timelineStart: clamped })
}

const handleFieldBlur = () => {
  applyPosition(fieldsToSeconds())
}

const nudgeField = (field: 'mins' | 'secs' | 'frac' | 'ms', delta: number) => {
  const current = fieldsToSeconds()
  const step = field === 'mins' ? 60 : field === 'secs' ? 1 : field === 'frac' ? 0.01 : 0.001
  applyPosition(current + delta * step)
}

const openRename = () => {
  editName.value = props.clip.name || ''
  positionToFields(props.clip.timelineStart)
  showRenameDialog.value = true
}

const handleRename = async () => {
  try {
    await mixStore.updateClip(props.clip.id, { name: editName.value.trim() || '' })
    showRenameDialog.value = false
  } catch (error) {
    console.error(error)
  }
}

const handleToggleLock = async () => {
  try {
    await mixStore.updateClip(props.clip.id, { isLocked: !props.clip.isLocked })
  } catch (error) {
    console.error(error)
  }
}

const handleDuplicate = async () => {
  try {
    await mixStore.duplicateClip(props.clip.id, mixStore.currentTime)
    showDuplicateDialog.value = false
  } catch (error) {
    console.error(error)
  }
}

const showClipDownloadDialog = ref(false)

const clipMidiJson = computed(() => {
  const c = props.clip
  if (!c.isAiGenerated || !c.activePromptId) return null
  const active = c.prompts.find(p => p.id === c.activePromptId)
  return active?.geminiResponse || null
})

function clipFilenameBase(): string {
  const slug = (s: string | undefined | null) => (s || '').replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase()
  return [songStore.currentSong?.title, mixStore.currentMix?.title, props.clip.name || `clip-${props.clip.id}`]
    .map(slug)
    .filter(Boolean)
    .join('_')
}

const handleDownload = () => {
  // Always open the dialog now — it offers WAV / MIDI / Add to library.
  // (MIDI is only enabled when the clip has an active prompt with MIDI JSON.)
  showClipDownloadDialog.value = true
}

const handleClipDownloadChoice = async (choice: 'midi' | 'wav' | 'library') => {
  if (choice === 'library') {
    // Open the name prompt sub-dialog without closing the main one — we'll close
    // both when the user confirms the name.
    libraryClipName.value = (props.clip.name || `clip-${props.clip.id}`) + '-Copy'
    showAddToLibraryDialog.value = true
    return
  }
  showClipDownloadDialog.value = false
  if (choice === 'midi') await downloadClipAsMidi()
  else await downloadClipAsWav()
}

const showAddToLibraryDialog = ref(false)
const libraryClipName = ref('')
const addingToLibrary = ref(false)

const handleAddToLibraryConfirm = async () => {
  const name = libraryClipName.value.trim()
  if (!name) return
  addingToLibrary.value = true
  try {
    const { useUserLibraryStore } = await import('../../stores/userLibraryStore')
    const libStore = useUserLibraryStore()
    await libStore.addFromClip(props.clip.id, name)
    showAddToLibraryDialog.value = false
    showClipDownloadDialog.value = false
    uiStore.showToast('success', 'Library', `Added "${name}" to your clip library.`)
  } catch (e: any) {
    uiStore.showToast('error', 'Library', e?.response?.data?.error ?? e?.message ?? 'Failed to add clip to library.')
  } finally {
    addingToLibrary.value = false
  }
}

const downloadClipAsWav = async () => {
  const url = `${STATIC_BASE_URL}${props.clip.filePath}`
  const suggestedName = `${clipFilenameBase()}.wav`

  const showPicker = (window as any).showSaveFilePicker
  if (typeof showPicker === 'function') {
    try {
      const handle = await showPicker({
        suggestedName,
        types: [{ description: 'Audio file', accept: { 'audio/wav': ['.wav'] } }],
      })
      const response = await fetch(url)
      if (!response.ok) throw new Error(`Download failed: ${response.status}`)
      const blob = await response.blob()
      const writable = await handle.createWritable()
      await writable.write(blob)
      await writable.close()
      return
    } catch (e: any) {
      if (e?.name === 'AbortError') return
      console.error('showSaveFilePicker failed, falling back to direct download:', e)
    }
  }

  const a = document.createElement('a')
  a.href = url
  a.download = suggestedName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

const downloadClipAsMidi = async () => {
  const midiJson = clipMidiJson.value
  if (!midiJson) return
  try {
    const { buildTrackMidiFile } = await import('../../utils/midi')
    // Single-clip export: use timelineStart=0 so notes start at 0, not at the
    // clip's mix-timeline position. The user wants the clip's own MIDI, not
    // the clip embedded in the larger mix timeline.
    const bpm = mixStore.currentMix?.bpm ?? 120
    const bytes = buildTrackMidiFile([{ timelineStart: 0, midiJson }], bpm)
    const blob = new Blob([bytes as BlobPart], { type: 'audio/midi' })
    const suggestedName = `${clipFilenameBase()}.mid`

    const showPicker = (window as any).showSaveFilePicker
    if (typeof showPicker === 'function') {
      try {
        const handle = await showPicker({
          suggestedName,
          types: [{ description: 'MIDI file', accept: { 'audio/midi': ['.mid'] } }],
        })
        const writable = await handle.createWritable()
        await writable.write(blob)
        await writable.close()
        return
      } catch (e: any) {
        if (e?.name === 'AbortError') return
        console.error('showSaveFilePicker failed, falling back to direct download:', e)
      }
    }

    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = suggestedName
    a.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Failed to build MIDI:', error)
  }
}

const handleBounce = async () => {
  bouncing.value = true
  try {
    await mixStore.bounceClip(props.clip.id)
    showBounceDialog.value = false
  } catch (error) {
    console.error(error)
  } finally {
    bouncing.value = false
  }
}

const handleAdjust = async () => {
  if (!adjustPrompt.value.trim()) return
  adjusting.value = true
  adjustError.value = ''
  try {
    await mixStore.adjustAiClip(props.clip.id, adjustPrompt.value.trim())
    adjustPrompt.value = ''
  } catch (error: any) {
    adjustError.value = error.response?.data || error.message || 'Adjustment failed'
  } finally {
    adjusting.value = false
  }
}

const reverting = ref(false)

const copyPrompt = (text: string) => {
  navigator.clipboard.writeText(text)
}

const handleRevert = async (promptId: number) => {
  reverting.value = true
  adjustError.value = ''
  try {
    await mixStore.revertClipPrompt(props.clip.id, promptId)
  } catch (error: any) {
    adjustError.value = error.response?.data || error.message || 'Revert failed'
  } finally {
    reverting.value = false
  }
}
</script>

<template>
  <div
    class="absolute group shadow-sm transition-colors select-none overflow-hidden"
    :class="[
      clip.isLocked ? 'bg-slate-100' : 'bg-teal-50',
      clip.isLocked ? '' : razorMode ? 'z-10 cursor-crosshair' : isDragging ? 'z-50 ring-2 ring-teal-500 cursor-grabbing' : 'cursor-grab',
      clip.isLocked ? 'cursor-default' : '',
      isDragging && mixStore.dragState && mixStore.dragState.destTrackId !== mixStore.dragState.sourceTrackId ? 'opacity-30' : '',
      horizontal
        ? clip.isLocked ? 'top-0 bottom-0 border-x border-slate-300' : 'top-0 bottom-0 border-x border-teal-200 hover:border-teal-400'
        : clip.isLocked ? 'left-0 right-0 border-y border-slate-300' : 'left-0 right-0 border-y border-teal-200 hover:border-teal-400'
    ]"
    :style="clipStyle"
    @mousedown="handleMouseDown"
    @mousemove="handleMouseMoveLocal"
    @mouseleave="handleRazorMouseLeave"
    @touchstart="handleTouchStart"
    @contextmenu="handleContextMenu"
  >
    <canvas ref="canvas" class="w-full h-full opacity-40 pointer-events-none" />

    <!-- Clip Label -->
    <div :class="horizontal
      ? `absolute top-1 left-2 text-[20px] font-black uppercase tracking-tighter pointer-events-none whitespace-nowrap ${clip.isLocked ? 'text-slate-500' : 'text-teal-600'}`
      : `absolute top-1 right-1 text-[14px] font-black uppercase tracking-tighter pointer-events-none whitespace-nowrap ${clip.isLocked ? 'text-slate-500' : 'text-teal-600'}`"
      :style="!horizontal ? { writingMode: 'vertical-lr' } : undefined">
      <i v-if="clip.isAiGenerated" class="pi pi-sparkles mr-1"></i>{{ clip.isLocked ? '[' : '' }}{{ clip.name || `Clip #${clip.id}` }}{{ clip.isLocked ? ']' : '' }}
    </div>

    <!-- Razor cut indicator -->
    <div v-if="razorMode && razorHoverPos !== null"
         class="absolute bg-red-500 pointer-events-none z-20"
         :class="horizontal ? 'top-0 bottom-0 w-px' : 'left-0 right-0 h-px'"
         :style="horizontal ? { left: `${razorHoverPos * pixelsPerSecond}px` } : { top: `${razorHoverPos * pixelsPerSecond}px` }">
    </div>

    <!-- Edit Trigger -->
    <button
      v-if="!readonly && !razorMode"
      class="absolute w-6 h-6 bg-white border border-teal-100 text-teal-600 rounded-md flex items-center justify-center hover:bg-teal-50 z-10"
      :class="horizontal ? 'bottom-1 left-9' : 'top-9 left-0.5'"
      @click.stop="openRename"
      v-tooltip.top="'Edit clip'"
    >
      <i class="pi pi-pencil text-[9px]"></i>
    </button>

    <!-- Duplicate Trigger -->
    <button
      v-if="!readonly && !razorMode"
      class="absolute w-6 h-6 bg-white border border-blue-200 rounded-md flex items-center justify-center hover:bg-blue-50 z-10"
      style="color: #0080ff;"
      :class="horizontal ? 'bottom-1 left-[4.25rem]' : 'top-[4.25rem] left-0.5'"
      @click.stop="showDuplicateDialog = true"
      v-tooltip.top="'Duplicate clip'"
    >
      <i class="pi pi-copy text-[9px]"></i>
    </button>

    <!-- Bounce Trigger (AI clips only) -->
    <button
      v-if="!readonly && !razorMode && clip.isAiGenerated"
      class="absolute w-6 h-6 bg-white border border-yellow-200 rounded-md flex items-center justify-center hover:bg-yellow-50 z-10"
      style="color: #f0c000;"
      :class="horizontal ? 'bottom-1 left-[6.25rem]' : 'top-[6.25rem] left-0.5'"
      @click.stop="showBounceDialog = true"
      v-tooltip.top="'Bounce to simple clip'"
    >
      <i class="pi pi-bullseye text-[9px]"></i>
    </button>

    <!-- Download Trigger -->
    <button
      v-if="!readonly && !razorMode"
      class="absolute w-6 h-6 bg-white border border-sky-100 text-sky-600 rounded-md flex items-center justify-center hover:bg-sky-50 z-10"
      :class="horizontal ? (clip.isAiGenerated ? 'bottom-1 left-[8.25rem]' : 'bottom-1 left-[6.25rem]') : (clip.isAiGenerated ? 'top-[8.25rem] left-0.5' : 'top-[6.25rem] left-0.5')"
      @click.stop="handleDownload"
      v-tooltip.top="'Download clip'"
    >
      <i class="pi pi-download text-[9px]"></i>
    </button>

    <!-- Lock Trigger -->
    <button
      v-if="!readonly && !razorMode"
      class="absolute w-6 h-6 bg-white border rounded-md flex items-center justify-center z-10"
      :class="[
        clip.isLocked ? 'border-amber-200 text-amber-600 hover:bg-amber-50' : 'border-amber-100 text-amber-500 hover:bg-amber-50',
        horizontal ? (clip.isAiGenerated ? 'bottom-1 left-[10.25rem]' : 'bottom-1 left-[8.25rem]') : (clip.isAiGenerated ? 'top-[10.25rem] left-0.5' : 'top-[8.25rem] left-0.5')
      ]"
      @click.stop="handleToggleLock"
      v-tooltip.top="clip.isLocked ? 'Unlock clip' : 'Lock clip'"
    >
      <i :class="clip.isLocked ? 'pi pi-lock' : 'pi pi-lock-open'" class="text-[9px]"></i>
    </button>

    <!-- AI Edit Trigger (AI clips only) -->
    <button
      v-if="!readonly && !razorMode && clip.isAiGenerated"
      class="absolute w-6 h-6 bg-white border border-pink-200 text-pink-500 rounded-md flex items-center justify-center hover:bg-pink-50 z-10"
      :class="horizontal ? 'bottom-1 left-[12.25rem]' : 'top-[12.25rem] left-0.5'"
      @click.stop="showAiEditDialog = true"
      v-tooltip.top="'AI Edit'"
    >
      <i class="pi pi-sparkles text-[9px]"></i>
    </button>

    <!-- Delete Trigger -->
    <button
      v-if="!readonly && !razorMode"
      class="absolute w-6 h-6 bg-white border border-red-100 text-red-500 rounded-md flex items-center justify-center hover:bg-red-50 z-10"
      :class="horizontal ? 'bottom-1 left-1' : 'top-1 left-0.5'"
      @click.stop="confirmDelete"
      v-tooltip.top="'Delete clip'"
    >
      <i class="pi pi-times text-[8px]"></i>
    </button>

    <!-- Clip Download / Library Dialog -->
    <Dialog v-model:visible="showClipDownloadDialog" modal header="Save clip" :style="{ width: '24rem' }">
      <p class="text-sm text-slate-600 mb-4">
        Pick what you want to do with this clip.
      </p>
      <div class="flex flex-col gap-2">
        <Button type="button" label="Save as WAV" icon="pi pi-volume-up" class="w-full" @click="handleClipDownloadChoice('wav')" />
        <Button type="button" label="Save as MIDI" icon="pi pi-code" severity="secondary" class="w-full" :disabled="!clipMidiJson" @click="handleClipDownloadChoice('midi')" v-tooltip.bottom="!clipMidiJson ? 'Only available for AI-generated clips' : undefined" />
        <Button type="button" label="Add to my library" icon="pi pi-bookmark" severity="secondary" class="w-full" :disabled="!clip.isAiGenerated" @click="handleClipDownloadChoice('library')" v-tooltip.bottom="!clip.isAiGenerated ? 'Only AI-generated clips can be added to your library' : undefined" />
      </div>
    </Dialog>

    <!-- Add to Library — name prompt -->
    <Dialog v-model:visible="showAddToLibraryDialog" modal header="Add to library" :style="{ width: '22rem' }">
      <div class="flex flex-col gap-3 py-2">
        <label class="text-xs text-slate-500">Name this clip</label>
        <InputText v-model="libraryClipName" class="w-full" autofocus @keyup.enter="handleAddToLibraryConfirm" />
        <div class="flex justify-end gap-2 mt-2">
          <Button type="button" label="Cancel" severity="secondary" @click="showAddToLibraryDialog = false" :disabled="addingToLibrary" />
          <Button type="button" label="Add" icon="pi pi-bookmark" :loading="addingToLibrary" :disabled="!libraryClipName.trim()" @click="handleAddToLibraryConfirm" />
        </div>
      </div>
    </Dialog>

    <!-- Clip Delete Confirmation Dialog -->
    <Dialog v-model:visible="showDeleteConfirm" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-exclamation-triangle text-red-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Remove clip?</h2>
        <p class="text-sm text-slate-500 mb-8 px-4">This will permanently delete this audio clip from the track.</p>

        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Delete Clip" severity="danger" class="w-full py-3 font-bold" @click="handleDelete" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDeleteConfirm = false" />
        </div>
      </div>
    </Dialog>

    <!-- Split Blocked Dialog -->
    <Dialog v-model:visible="showSplitBlockedDialog" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-amber-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-info-circle text-amber-500" style="font-size: 5rem"></i>
        </div>
        <p class="text-sm text-slate-600 mb-8 px-4">Splitting prompt generated clips is not supported at this time, please bounce to simple clip.</p>
        <Button label="OK" class="w-full py-3 font-bold" @click="showSplitBlockedDialog = false" />
      </div>
    </Dialog>

    <!-- Duplicate Confirmation Dialog -->
    <Dialog v-model:visible="showDuplicateDialog" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-blue-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-copy text-blue-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-8">Copy this clip to cursor?</h2>
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Copy Clip" class="w-full py-3 font-bold" @click="handleDuplicate" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showDuplicateDialog = false" />
        </div>
      </div>
    </Dialog>

    <!-- Bounce Confirmation Dialog -->
    <Dialog v-model:visible="showBounceDialog" modal :closable="false" :style="{ width: '22rem' }">
      <div class="flex flex-col items-center text-center py-6">
        <div class="w-32 h-32 bg-purple-50 rounded-full flex items-center justify-center mb-6">
          <i class="pi pi-bullseye text-purple-500" style="font-size: 5rem"></i>
        </div>
        <h2 class="text-xl font-bold text-slate-900 mb-4">Bounce clip?</h2>
        <p class="text-sm text-slate-600 mb-8 px-4">You are about to bounce this clip. All prompt information will be lost.</p>
        <div class="flex flex-col gap-3 w-full">
          <Button label="Yes, Bounce Clip" severity="warn" class="w-full py-3 font-bold" @click="handleBounce" :loading="bouncing" />
          <Button label="Cancel" severity="secondary" text class="w-full font-medium" @click="showBounceDialog = false" :disabled="bouncing" />
        </div>
      </div>
    </Dialog>

    <!-- Clip Properties Dialog -->
    <Dialog v-model:visible="showRenameDialog" modal header="Clip Properties" :style="{ width: '28rem' }">
      <div class="flex flex-col gap-4 pt-2">
        <div class="flex flex-col gap-2">
          <label for="clipName" class="font-semibold text-sm">Name</label>
          <InputText id="clipName" v-model="editName" placeholder="Clip name" class="w-full" @keydown.enter="handleRename" />
        </div>
        <div class="flex flex-col gap-2">
          <label class="font-semibold text-sm">Position</label>
          <div class="flex items-center justify-center gap-1">
            <!-- Minutes -->
            <div class="flex flex-col items-center gap-1">
              <Button icon="pi pi-plus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeField('mins', 1)" />
              <InputText v-model="posMins" class="!w-10 text-center font-mono !p-1" maxlength="2" @blur="handleFieldBlur" @keydown.enter="handleFieldBlur" @keydown="($event: KeyboardEvent) => { if (!/\d/.test($event.key) && !['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Enter','Home','End'].includes($event.key)) $event.preventDefault() }" />
              <Button icon="pi pi-minus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeField('mins', -1)" />
            </div>
            <span class="font-mono text-lg font-bold pb-0.5">:</span>
            <!-- Seconds -->
            <div class="flex flex-col items-center gap-1">
              <Button icon="pi pi-plus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeField('secs', 1)" />
              <InputText v-model="posSecs" class="!w-10 text-center font-mono !p-1" maxlength="2" @blur="handleFieldBlur" @keydown.enter="handleFieldBlur" @keydown="($event: KeyboardEvent) => { if (!/\d/.test($event.key) && !['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Enter','Home','End'].includes($event.key)) $event.preventDefault() }" />
              <Button icon="pi pi-minus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeField('secs', -1)" />
            </div>
            <span class="font-mono text-lg font-bold pb-0.5">.</span>
            <!-- Fractions -->
            <div class="flex flex-col items-center gap-1">
              <Button icon="pi pi-plus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeField('frac', 1)" />
              <InputText v-model="posFrac" class="!w-10 text-center font-mono !p-1" maxlength="2" @blur="handleFieldBlur" @keydown.enter="handleFieldBlur" @keydown="($event: KeyboardEvent) => { if (!/\d/.test($event.key) && !['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Enter','Home','End'].includes($event.key)) $event.preventDefault() }" />
              <Button icon="pi pi-minus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeField('frac', -1)" />
            </div>
            <!-- Milliseconds -->
            <div class="flex flex-col items-center gap-1">
              <Button icon="pi pi-plus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeField('ms', 1)" />
              <InputText v-model="posMs" class="!w-7 text-center font-mono !p-1" maxlength="1" @blur="handleFieldBlur" @keydown.enter="handleFieldBlur" @keydown="($event: KeyboardEvent) => { if (!/\d/.test($event.key) && !['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Enter','Home','End'].includes($event.key)) $event.preventDefault() }" />
              <Button icon="pi pi-minus" severity="secondary" text rounded size="small" class="!w-7 !h-7 !p-0" @click="nudgeField('ms', -1)" />
            </div>
          </div>
        </div>
        <div class="flex gap-3 justify-end">
          <Button label="Cancel" severity="secondary" text @click="showRenameDialog = false" />
          <Button label="Save" @click="handleRename" />
        </div>
      </div>
    </Dialog>

    <!-- AI Edit Dialog -->
    <Dialog v-if="clip.isAiGenerated" v-model:visible="showAiEditDialog" modal :style="{ width: '28rem' }">
      <template #header>
        <div class="flex items-center gap-2">
          <i class="pi pi-sparkles text-pink-500"></i>
          <span class="font-semibold">AI Edit</span>
        </div>
      </template>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label class="font-semibold text-sm">Adjust AI</label>
          <Textarea v-model="adjustPrompt" rows="4" class="w-full text-sm" placeholder="e.g. Replace hi-hat with ride cymbal" :disabled="adjusting || reverting" @keydown.ctrl.enter="handleAdjust" />
          <Button icon="pi pi-send" label="Adjust" @click="handleAdjust" :loading="adjusting" :disabled="!adjustPrompt.trim() || adjusting || reverting" class="w-full" />
          <small v-if="adjustError" class="text-red-500">{{ adjustError }}</small>
        </div>

        <div class="flex flex-col gap-2">
          <label class="font-semibold text-sm">Prompt History</label>
          <div class="max-h-60 overflow-y-auto flex flex-col gap-2 p-1">
            <div v-for="p in [...clip.prompts].sort((a, b) => b.sequence - a.sequence)" :key="p.id"
                 class="rounded p-2 text-xs"
                 :class="p.id === clip.activePromptId ? 'bg-teal-50 ring-1 ring-teal-300' : 'bg-slate-50'">
              <div class="flex items-start justify-between gap-1">
                <div class="font-medium text-slate-700 flex-1">
                  {{ p.prompt }}
                  <span v-if="p.id === clip.activePromptId" class="text-teal-600 text-[10px] font-bold ml-1">ACTIVE</span>
                </div>
                <div class="flex items-center gap-1 shrink-0">
                  <button class="w-5 h-5 bg-white border border-slate-200 text-slate-500 rounded-md flex items-center justify-center hover:bg-slate-100 hover:text-slate-700" @click="copyPrompt(p.prompt)" v-tooltip.top="'Copy prompt'">
                    <i class="pi pi-copy text-[10px]"></i>
                  </button>
                  <button v-if="p.status === 'Completed' && p.id !== clip.activePromptId"
                          class="w-5 h-5 bg-white border border-slate-200 text-slate-500 rounded-md flex items-center justify-center hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
                          @click="handleRevert(p.id)"
                          :disabled="reverting || adjusting"
                          v-tooltip.top="'Revert to this prompt'">
                    <i class="pi pi-replay text-[10px]"></i>
                  </button>
                </div>
              </div>
              <div class="flex items-center gap-2 mt-1">
                <span class="text-slate-400">{{ new Date(p.createdAt).toLocaleString() }}</span>
                <span :class="p.status === 'Completed' ? 'text-teal-500' : p.status === 'Failed' ? 'text-red-500' : 'text-amber-500'" class="text-[10px] italic">{{ p.status === 'Completed' ? 'completed' : p.status === 'Failed' ? 'failed' : 'pending...' }}</span>
                <span v-if="p.model" :class="p.model === 'Advanced' ? 'bg-violet-100 text-violet-700 prompt-meta-badge' : 'bg-slate-200 text-slate-600'" class="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded">{{ p.model === 'Advanced' ? 'ADV' : 'STD' }}</span>
                <span v-if="p.thinking && p.status === 'Completed'" class="text-[9px] font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 prompt-meta-badge" v-tooltip.top="'Reasoning enabled'">T</span>
                <span v-if="p.cost != null" :class="p.model ? '' : 'ml-auto'" class="text-[10px] text-slate-400">${{ p.cost.toFixed(4) }}</span>
              </div>
            </div>
            <div v-if="!clip.prompts.length" class="text-xs text-slate-400 italic">No prompts recorded</div>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>
